import { getMediaActionIcon } from '@components/MediaActionBar/mediaActionIcons';
import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import type {
  MediaMenuAction,
  MediaMenuActionItem,
  MediaMenuHostItem,
} from '@lib/media/mediaActions';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import css from './MobileMediaMenuDrawer.module.scss';

type MobileMediaMenuDrawerProps = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  media: Media;
  /**
   * The rows, in groups separated by a hairline. Most hosts pass a single group; the moment
   * modal passes two (verbs, then housekeeping), mirroring the divider its desktop kebab shows.
   *
   * Groups rather than a flat list plus a "divider after this id" marker: the split is a fact
   * about how the caller *built* the list — `splitMediaActions` already returns the two arrays —
   * so passing them separately means nothing has to re-derive the boundary from an id.
   *
   * Empty groups are dropped, so a caller need not branch on whether its verb group is empty.
   */
  groups: MediaMenuActionItem[][];
  /**
   * Rows the host supplies itself, in a final group of their own. They carry their own glyph
   * and handler because they are not `MediaMenuAction`s — see `MediaMenuHostItem`.
   *
   * Kept beside `groups` rather than folded into it so the desktop menu and this drawer render
   * the same array (`.claude/rules/context-menu-actions.md`); `MediaActionBar` passes one list
   * to both.
   */
  hostItems?: MediaMenuHostItem[];
  onAction: (action: MediaMenuAction) => void;
};

export const MobileMediaMenuDrawer = ({
  open,
  onClose,
  onOpen,
  media,
  groups,
  hostItems,
  onAction,
}: MobileMediaMenuDrawerProps) => {
  const title = media.metadata?.name || 'Media settings';
  const filledGroups = groups.filter((group) => group.length > 0);

  return (
    <MobileDrawer open={open} onClose={onClose} onOpen={onOpen} frosted>
      <div className={css.content}>
        <span className={css.title}>{title}</span>
        {filledGroups.map((group, index) => (
          <div
            // Index is a safe key here: the group list is positional by construction (verbs,
            // then housekeeping) and never reordered or filtered between renders.
            key={index}
            className={classNames(css.actions, index > 0 && css.groupDivider)}
          >
            {group.map((item) => (
              <button
                key={item.id}
                className={classNames(css.actionItem, {
                  [css.disabled]: item.disabled,
                  [css.danger]: item.danger,
                })}
                disabled={item.disabled}
                onClick={() => {
                  onAction(item.id);
                  onClose();
                }}
              >
                <span className={css.actionIcon}>{getMediaActionIcon(item.id, media)}</span>
                <span className={css.actionLabel}>{item.label}</span>
                {/* Indexing hint is not shown on mobile — tooltips don't work well on touch;
                    the disabled state alone communicates that the action is unavailable. */}
              </button>
            ))}
          </div>
        ))}
        {hostItems && hostItems.length > 0 && (
          <div className={classNames(css.actions, filledGroups.length > 0 && css.groupDivider)}>
            {hostItems.map((item) => (
              <button
                key={item.id}
                className={css.actionItem}
                onClick={() => {
                  item.onSelect();
                  onClose();
                }}
              >
                <span className={css.actionIcon}>{item.icon}</span>
                <span className={css.actionLabel}>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </MobileDrawer>
  );
};
